import { useEffect, useRef, useState } from 'react';

const DateInput = ({ value, onChange, disabled }) => {
	const [date, setDate] = useState(() => {
		const d = value ? new Date(value) : new Date();
		return {
			day: d.getDate(),
			month: d.getMonth() + 1, 
			year: d.getFullYear()
		};
	});

	const monthRef = useRef(null);
	const dayRef = useRef(null);
	const yearRef = useRef(null);

	useEffect(() => {
		const d = value ? new Date(value) : new Date();
		setDate({
			day: d.getDate(),
			month: d.getMonth() + 1,
			year: d.getFullYear()
		});
	}, [value]);

	const isDateDisabled = (y, m, d) => {
		if (!disabled) return false;
		const candidate = new Date(y, m - 1, d);
		candidate.setHours(0, 0, 0, 0);

		if (disabled.after && candidate > disabled.after) return true;
		if (disabled.before && candidate < disabled.before) return true;
		return false;
	};

	const validateDate = (field, numValue) => {
		if (
			(field === 'day' && (numValue < 1 || numValue > 31)) ||
			(field === 'month' && (numValue < 1 || numValue > 12)) ||
			(field === 'year' && (numValue < 1000 || numValue > 9999))
		) {
			return false;
		}

		const tentative = { ...date, [field]: numValue };
		const jsDate = new Date(tentative.year, tentative.month - 1, tentative.day);

		const valid =
			jsDate.getFullYear() === tentative.year &&
			jsDate.getMonth() + 1 === tentative.month &&
			jsDate.getDate() === tentative.day;

		return valid && !isDateDisabled(tentative.year, tentative.month, tentative.day);
	};

	const handleInputChange = (field) => (e) => {
		const inputValue = e.target.value;
		const numValue = inputValue === '' ? '' : Number(inputValue);
		const isValid = typeof numValue === 'number' && validateDate(field, numValue);

		const newDate = { ...date, [field]: numValue };
		setDate(newDate);

		if (isValid) {
			onChange(new Date(newDate.year, newDate.month - 1, newDate.day));
		}
	};

	const initialDate = useRef(date);

	const handleBlur = (field) => (e) => {
		if (!e.target.value) {
			setDate(initialDate.current);
			return;
		}

		const numValue = Number(e.target.value);
		const isValid = validateDate(field, numValue);

		if (!isValid) {
			setDate(initialDate.current);
		} else {
			initialDate.current = { ...date, [field]: numValue };
		}
	};

	const handleKeyDown = (field) => (e) => {
		// Allow command (or control) combinations
		if (e.metaKey || e.ctrlKey) return;

		// Prevent non-numeric characters, excluding allowed keys
		if (
			!/^[0-9]$/.test(e.key) &&
			![
				'ArrowUp',
				'ArrowDown',
				'ArrowLeft',
				'ArrowRight',
				'Delete',
				'Tab',
				'Backspace',
				'Enter'
			].includes(e.key)
		) {
			e.preventDefault();
			return;
		}

		if (e.key === 'ArrowUp') {
			e.preventDefault();
			let newDate = { ...date };

			if (field === 'day') {
				const lastDayOfMonth = new Date(date.year, date.month, 0).getDate();
				if (date.day === lastDayOfMonth) {
					newDate = { ...newDate, day: 1, month: (date.month % 12) + 1 };
					if (newDate.month === 1) newDate.year += 1;
				} else {
					newDate.day += 1;
				}
			} else if (field === 'month') {
				if (date.month === 12) {
					newDate = { ...newDate, month: 1, year: date.year + 1 };
				} else {
					newDate.month += 1;
				}
			} else if (field === 'year') {
				newDate.year += 1;
			}

			setDate(newDate);
			onChange(new Date(newDate.year, newDate.month - 1, newDate.day));
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			let newDate = { ...date };

			if (field === 'day') {
				if (date.day === 1) {
					newDate.month -= 1;
					if (newDate.month === 0) {
						newDate.month = 12;
						newDate.year -= 1;
					}
					newDate.day = new Date(newDate.year, newDate.month, 0).getDate();
				} else {
					newDate.day -= 1;
				}
			} else if (field === 'month') {
				if (date.month === 1) {
					newDate = { ...newDate, month: 12, year: date.year - 1 };
				} else {
					newDate.month -= 1;
				}
			} else if (field === 'year') {
				newDate.year -= 1;
			}

			setDate(newDate);
			onChange(new Date(newDate.year, newDate.month - 1, newDate.day));
		} else if (e.key === 'ArrowRight') {
			const { selectionStart, value } = e.currentTarget;
			if (
				selectionStart === value.length ||
				(selectionStart === 0 && e.currentTarget.selectionEnd === value.length)
			) {
				e.preventDefault();
				if (field === 'month') dayRef.current?.focus();
				if (field === 'day') yearRef.current?.focus();
			}
		} else if (e.key === 'ArrowLeft') {
			if (e.currentTarget.selectionStart === 0) {
				e.preventDefault();
				if (field === 'day') monthRef.current?.focus();
				if (field === 'year') dayRef.current?.focus();
			}
		}
	};

	return (
		<div className="flex border rounded-lg items-center text-sm px-1">
			<input
				type="text"
				ref={dayRef}
				maxLength={2}
				value={date.day.toString()}
				onChange={handleInputChange('day')}
				onKeyDown={handleKeyDown('day')}
				onFocus={(e) => {
					if (window.innerWidth > 1024) {
						e.target.select();
					}
				}}
				onBlur={handleBlur('day')}
				className="p-0 outline-none w-7 border-none text-center"
				placeholder="D"
			/>
			<span className="opacity-20 -mx-px">/</span>
			<input
				type="text"
				ref={monthRef}
				maxLength={2}
				value={date.month.toString()}
				onChange={handleInputChange('month')}
				onKeyDown={handleKeyDown('month')}
				onFocus={(e) => {
					if (window.innerWidth > 1024) {
						e.target.select();
					}
				}}
				onBlur={handleBlur('month')}
				className="p-0 outline-none w-6 border-none text-center"
				placeholder="M"
			/>
			<span className="opacity-20 -mx-px">/</span>
			<input
				type="text"
				ref={yearRef}
				maxLength={4}
				value={date.year.toString()}
				onChange={handleInputChange('year')}
				onKeyDown={handleKeyDown('year')}
				onFocus={(e) => {
					if (window.innerWidth > 1024) {
						e.target.select();
					}
				}}
				onBlur={handleBlur('year')}
				className="p-0 outline-none w-12 border-none text-center"
				placeholder="YYYY"
			/>
		</div>
	);
};

export { DateInput };